/**
 * Hands-on play activities — no screens needed.
 *
 * Curated alternatives for facilitators who want to skip the digital
 * games and lean into nature, pretend play, fort-building, drawing,
 * story-making, movement, or sound exploration.
 *
 * Each activity is bilingual (en/es) and built for ages 3–6 — kids who
 * are just beginning to read and rapidly building vocabulary. Every
 * activity surfaces a small set of **vocabulary words** the teacher can
 * introduce in both languages during the play, plus simple steps that
 * preserve the Wonderpath approach (child-led, observe-first,
 * specific-praise, no samples, care for life).
 *
 * Shape mirrors `SuggestedPhase` so a teacher can drop any activity
 * straight into the lesson flow as a phase.
 */

export type PlayCategory =
  | 'nature'
  | 'pretend'
  | 'fort'
  | 'drawing'
  | 'story'
  | 'movement'
  | 'sound';

export interface PlayActivity {
  id: string;
  category: PlayCategory;
  /** Lesson-flow phase title (also used in the Add chip). */
  name: { en: string; es: string };
  /** Suggested duration in minutes (teacher can edit after adding). */
  duration: number;
  /** Two-to-four sentence description used in the lesson preview + in-session header. */
  description: { en: string; es: string };
  /** Plain-language materials list — uses common stuff teachers already have. */
  materials: { en: string[]; es: string[] };
  /** In-session prompt cards — the teacher's read-aloud cues during play. */
  prompts: { en: string[]; es: string[] };
  /** Words to introduce / repeat in both languages while the play unfolds. */
  vocabulary: { en: string[]; es: string[] };
}

export const PLAY_ACTIVITIES: PlayActivity[] = [
  // ── NATURE ─────────────────────────────────────────────────────────
  {
    id: 'nature-treasure-hunt',
    category: 'nature',
    name: { en: 'Outside treasure hunt', es: 'Búsqueda de tesoros afuera' },
    duration: 12,
    description: {
      en: 'Each child finds three things outside they want to bring back: something soft, something rough, something that catches the light. Slow walking, careful looking. They show their treasures in a circle and name what they noticed.',
      es: 'Cada niño/a busca afuera tres cosas que quiera traer: algo suave, algo áspero, algo que atrapa la luz. Caminar despacio, mirar con cuidado. Muestran sus tesoros en círculo y nombran lo que notaron.',
    },
    materials: {
      en: ['A small basket, bag, or pocket per child', 'Outdoor space (yard, park, sidewalk edge, courtyard)'],
      es: ['Una cestita, bolsa o bolsillo por niño/a', 'Espacio al aire libre (jardín, parque, vereda, patio)'],
    },
    prompts: {
      en: [
        'Walk slowly together. "What do you notice that wasn\'t here yesterday?"',
        'Each child finds three things: one soft, one rough, one that catches the light.',
        'Return to the circle. Each child shows one treasure and names how it feels.',
      ],
      es: [
        'Caminen despacio juntos. "¿Qué notas que no estaba ayer aquí?"',
        'Cada niño/a busca tres cosas: una suave, una áspera, una que atrapa la luz.',
        'Vuelvan al círculo. Cada niño/a muestra un tesoro y nombra cómo se siente.',
      ],
    },
    vocabulary: {
      en: ['soft', 'rough', 'shiny', 'leaf', 'twig', 'pebble', 'feather'],
      es: ['suave', 'áspero/a', 'brillante', 'hoja', 'ramita', 'piedrita', 'pluma'],
    },
  },
  {
    id: 'nature-listening-sit',
    category: 'nature',
    name: { en: 'Listening sit', es: 'Escucha sentados' },
    duration: 5,
    description: {
      en: 'Find a spot outside (or by an open window). Sit quietly for one whole minute. Each child holds up fingers — one finger for each different sound they hear. Share what was heard, no right answers.',
      es: 'Encuentra un lugar afuera (o junto a una ventana abierta). Siéntense en silencio un minuto entero. Cada niño/a levanta los dedos — uno por cada sonido distinto que escuchó. Compartan lo que oyeron, sin respuestas correctas.',
    },
    materials: {
      en: ['Outdoor space or open window', 'A timer (phone, sand timer, or count to 60)'],
      es: ['Espacio al aire libre o ventana abierta', 'Un temporizador (teléfono, reloj de arena o contar hasta 60)'],
    },
    prompts: {
      en: [
        'Find a spot. Sit so your body is still and your ears are open.',
        'For one minute: count each new sound on your fingers. No talking.',
        'Share: "I heard…" — name the loudest, the closest, the gentlest.',
      ],
      es: [
        'Encuentra un lugar. Siéntate con el cuerpo quieto y los oídos abiertos.',
        'Durante un minuto: cuenta cada sonido nuevo con los dedos. Sin hablar.',
        'Comparte: "Escuché…" — nombra el más fuerte, el más cercano, el más suave.',
      ],
    },
    vocabulary: {
      en: ['sound', 'loud', 'soft', 'near', 'far', 'birdsong', 'wind'],
      es: ['sonido', 'fuerte', 'suave', 'cerca', 'lejos', 'canto', 'viento'],
    },
  },

  // ── PRETEND ────────────────────────────────────────────────────────
  {
    id: 'pretend-creature-day',
    category: 'pretend',
    name: { en: 'A day in your creature\'s life', es: 'Un día en la vida de tu criatura' },
    duration: 15,
    description: {
      en: 'Each child becomes their avatar creature for ten minutes — moving, eating, resting like that creature would. The teacher narrates softly: "Now the morning sun comes. What does your creature do first?" Children respond through the body, not words.',
      es: 'Cada niño/a se convierte en su criatura avatar por diez minutos — moverse, comer, descansar como lo haría esa criatura. La maestra/o narra suavemente: "Ahora sale el sol de la mañana. ¿Qué hace tu criatura primero?" Los niños responden con el cuerpo, no con palabras.',
    },
    materials: {
      en: ['Avatar cards (already assigned)', 'Open floor space'],
      es: ['Tarjetas de avatar (ya asignadas)', 'Espacio en el piso'],
    },
    prompts: {
      en: [
        'Find a space where you can move without bumping anyone.',
        '"The sun is rising. Show me how your creature wakes up."',
        '"It\'s noon. Your creature is hungry. What does it do?"',
        '"Evening now. Your creature finds its safe place to rest."',
      ],
      es: [
        'Encuentra un espacio donde puedas moverte sin chocar con nadie.',
        '"El sol está saliendo. Muéstrame cómo despierta tu criatura."',
        '"Es mediodía. Tu criatura tiene hambre. ¿Qué hace?"',
        '"Llegó la tarde. Tu criatura encuentra su lugar seguro para descansar."',
      ],
    },
    vocabulary: {
      en: ['morning', 'noon', 'evening', 'wake', 'rest', 'safe', 'home'],
      es: ['mañana', 'mediodía', 'tarde', 'despertar', 'descansar', 'seguro', 'hogar'],
    },
  },
  {
    id: 'pretend-shop-of-wonders',
    category: 'pretend',
    name: { en: 'Shop of wonders', es: 'Tienda de maravillas' },
    duration: 12,
    description: {
      en: 'Set up a tiny "shop" with leaves, pinecones, fabric scraps, paper. One child is the shopkeeper, others are buyers. Trade with words and feelings, not money. "I would like the smooth one for someone who is sad."',
      es: 'Arma una "tiendita" con hojas, piñas, recortes de tela, papel. Un niño/a es la tendera, otros son compradores. Intercambien con palabras y sentimientos, no con dinero. "Quiero el suave para alguien que está triste."',
    },
    materials: {
      en: ['Natural objects + fabric scraps + paper', 'A blanket or cloth as the "counter"'],
      es: ['Objetos naturales + recortes de tela + papel', 'Una manta o tela como "mostrador"'],
    },
    prompts: {
      en: [
        'Lay out the treasures on the counter. Each shopper takes a turn.',
        '"I am looking for something for [a friend / a happy day / my pet]."',
        'The shopkeeper picks one and tells why it fits. Trade roles.',
      ],
      es: [
        'Pongan los tesoros en el mostrador. Cada comprador/a toma su turno.',
        '"Busco algo para [una amiga / un día feliz / mi mascota]."',
        'La tendera elige uno y dice por qué le queda. Cambien de roles.',
      ],
    },
    vocabulary: {
      en: ['smooth', 'rough', 'gift', 'choose', 'because', 'feeling'],
      es: ['suave', 'áspero/a', 'regalo', 'elegir', 'porque', 'sentimiento'],
    },
  },

  // ── FORT ───────────────────────────────────────────────────────────
  {
    id: 'fort-soft-house',
    category: 'fort',
    name: { en: 'Build a soft house', es: 'Construir una casa suave' },
    duration: 18,
    description: {
      en: 'Together, build one fort big enough for everyone using blankets, cushions, chairs, boxes. The teacher does not show how — only asks questions: "What does our house need next? Who will be the door?" Inside, share one quiet story.',
      es: 'Juntos, construyan una casa-fuerte lo suficientemente grande para todos usando mantas, cojines, sillas, cajas. La maestra/o no muestra cómo — solo pregunta: "¿Qué necesita nuestra casa ahora? ¿Quién será la puerta?" Adentro, compartan una historia tranquila.',
    },
    materials: {
      en: ['Blankets or sheets', 'Cushions, pillows', 'Chairs or boxes for posts', 'Optional: clothespins or string'],
      es: ['Mantas o sábanas', 'Cojines, almohadas', 'Sillas o cajas para postes', 'Opcional: pinzas o cordel'],
    },
    prompts: {
      en: [
        'Gather the materials in the middle. "What does our house need first?"',
        'Build together — every voice gets one suggestion before we start.',
        'Once it stands, climb in. Share one quiet story or song inside.',
        'Before taking it apart, name one thing each person added.',
      ],
      es: [
        'Junten los materiales en el medio. "¿Qué necesita nuestra casa primero?"',
        'Construyan juntos — cada voz da una sugerencia antes de empezar.',
        'Cuando esté en pie, métanse. Compartan una historia o canción tranquila.',
        'Antes de desarmar, nombren una cosa que cada persona aportó.',
      ],
    },
    vocabulary: {
      en: ['build', 'inside', 'outside', 'roof', 'wall', 'together', 'cozy'],
      es: ['construir', 'adentro', 'afuera', 'techo', 'pared', 'juntos', 'acogedor/a'],
    },
  },
  {
    id: 'fort-creature-den',
    category: 'fort',
    name: { en: 'Make a den for your creature', es: 'Hacer una guarida para tu criatura' },
    duration: 12,
    description: {
      en: 'Each child builds a tiny den (size of two hands) for the creature on their avatar — using leaves, paper, fabric scraps, twigs. They show the den and name one thing the creature loves about it.',
      es: 'Cada niño/a construye una guaridita (del tamaño de dos manos) para la criatura de su avatar — con hojas, papel, recortes de tela, ramitas. Muestran la guarida y nombran una cosa que su criatura ama de ella.',
    },
    materials: {
      en: ['Leaves, twigs, pinecones', 'Fabric scraps, yarn', 'Paper, tape (optional)'],
      es: ['Hojas, ramitas, piñas', 'Recortes de tela, hilo', 'Papel, cinta (opcional)'],
    },
    prompts: {
      en: [
        '"Where would your creature feel safest? Show me with your hands first."',
        'Build the den. No instructions — just listen to what the creature needs.',
        'Each child shares: "My creature loves this den because…"',
      ],
      es: [
        '"¿Dónde se sentiría más segura tu criatura? Muéstrame primero con las manos."',
        'Construye la guarida. Sin instrucciones — solo escucha lo que necesita la criatura.',
        'Cada niño/a comparte: "A mi criatura le encanta esta guarida porque…"',
      ],
    },
    vocabulary: {
      en: ['safe', 'cozy', 'shelter', 'soft', 'warm', 'protect'],
      es: ['seguro/a', 'acogedor/a', 'refugio', 'suave', 'cálido/a', 'proteger'],
    },
  },

  // ── DRAWING / MAKING ───────────────────────────────────────────────
  {
    id: 'drawing-from-feeling',
    category: 'drawing',
    name: { en: 'Draw a feeling', es: 'Dibujar un sentimiento' },
    duration: 12,
    description: {
      en: 'No subjects. The teacher names one feeling — calm, surprised, brave, tired — and children draw whatever that feeling looks like to them. Lines, shapes, colors, no people required. Each child shares a single word about their drawing.',
      es: 'Sin temas. La maestra/o nombra un sentimiento — calmo, sorprendido, valiente, cansado — y los niños dibujan cómo se ve ese sentimiento para ellos. Líneas, formas, colores, sin personas requeridas. Cada niño/a comparte una sola palabra sobre su dibujo.',
    },
    materials: {
      en: ['Paper (any kind, even reused)', 'Crayons or colored pencils'],
      es: ['Papel (cualquier tipo, incluso reusado)', 'Crayones o lápices de colores'],
    },
    prompts: {
      en: [
        '"Today\'s feeling is [calm / surprised / brave / tired]. Close your eyes. What color is it?"',
        '"Draw what that feeling looks like — no people, just shapes and colors."',
        '"Now show your drawing to a neighbor. Each say one word about it."',
      ],
      es: [
        '"El sentimiento de hoy es [calmo/a / sorprendido/a / valiente / cansado/a]. Cierra los ojos. ¿De qué color es?"',
        '"Dibuja cómo se ve ese sentimiento — sin personas, solo formas y colores."',
        '"Ahora muestra tu dibujo a tu compañero/a. Cada uno/a dice una palabra."',
      ],
    },
    vocabulary: {
      en: ['feeling', 'calm', 'brave', 'surprised', 'tired', 'color', 'shape'],
      es: ['sentimiento', 'calmo/a', 'valiente', 'sorprendido/a', 'cansado/a', 'color', 'forma'],
    },
  },
  {
    id: 'drawing-portrait-of-place',
    category: 'drawing',
    name: { en: 'Portrait of a place', es: 'Retrato de un lugar' },
    duration: 15,
    description: {
      en: 'Each child draws the place they feel most at home — their bed, a tree, the kitchen, grandma\'s lap. Not a person, just the place. Then they name three things in their picture that are alive (people, plants, animals, even sunlight).',
      es: 'Cada niño/a dibuja el lugar donde se siente más en casa — su cama, un árbol, la cocina, el regazo de la abuela. No una persona, solo el lugar. Luego nombran tres cosas vivas en su dibujo (personas, plantas, animales, incluso la luz del sol).',
    },
    materials: {
      en: ['Paper', 'Crayons, markers, or colored pencils'],
      es: ['Papel', 'Crayones, marcadores o lápices de colores'],
    },
    prompts: {
      en: [
        '"Close your eyes. Where in the world do you feel most at home?"',
        'Draw the place. Don\'t draw any people. Just the place.',
        'When you finish, find three living things in your drawing — and name them.',
      ],
      es: [
        '"Cierra los ojos. ¿En qué lugar del mundo te sientes más en casa?"',
        'Dibuja el lugar. No dibujes personas. Solo el lugar.',
        'Cuando termines, encuentra tres cosas vivas en tu dibujo — y nómbralas.',
      ],
    },
    vocabulary: {
      en: ['home', 'place', 'living', 'plant', 'animal', 'sunlight', 'safe'],
      es: ['hogar', 'lugar', 'vivo/a', 'planta', 'animal', 'luz del sol', 'seguro/a'],
    },
  },

  // ── STORY ──────────────────────────────────────────────────────────
  {
    id: 'story-three-objects',
    category: 'story',
    name: { en: 'Three-object story', es: 'Cuento de tres objetos' },
    duration: 10,
    description: {
      en: 'Place three random objects in the middle (a leaf, a spoon, a sock — anything). Together, the group makes up one short story that uses all three. The teacher writes nothing down — the story is for the room.',
      es: 'Pon tres objetos al azar en el medio (una hoja, una cuchara, un calcetín — cualquier cosa). Juntos, el grupo inventa un cuento corto que use los tres. La maestra/o no escribe nada — el cuento es para el salón.',
    },
    materials: {
      en: ['Three random everyday objects'],
      es: ['Tres objetos cotidianos al azar'],
    },
    prompts: {
      en: [
        'Show the three objects. "These three are in our story today. Who wants to start?"',
        'Each child adds one sentence. Pass the turn around the circle.',
        'When the story finds an ending, end it with: "And then they all rested."',
      ],
      es: [
        'Muestra los tres objetos. "Estos tres están en nuestro cuento hoy. ¿Quién quiere empezar?"',
        'Cada niño/a agrega una oración. Pasen el turno por el círculo.',
        'Cuando el cuento encuentre su final, ciérrenlo con: "Y luego todos descansaron."',
      ],
    },
    vocabulary: {
      en: ['story', 'beginning', 'middle', 'ending', 'character', 'because', 'finally'],
      es: ['cuento', 'comienzo', 'medio', 'final', 'personaje', 'porque', 'finalmente'],
    },
  },

  // ── MOVEMENT ───────────────────────────────────────────────────────
  {
    id: 'movement-mirror-walk',
    category: 'movement',
    name: { en: 'Mirror walk', es: 'Caminata espejo' },
    duration: 6,
    description: {
      en: 'Pairs face each other. One leads with slow movement (an arm rising, a head tilt) and the partner mirrors. No talking, no fast moves. Switch leaders after a minute. Builds attention and gentleness.',
      es: 'En parejas, uno frente al otro. Uno guía con movimiento lento (un brazo que sube, una cabeza que se inclina) y la pareja lo refleja. Sin hablar, sin movimientos rápidos. Cambien de líder al minuto. Construye atención y suavidad.',
    },
    materials: {
      en: ['Open floor space', 'A soft sound or chime to mark the switch'],
      es: ['Espacio en el piso', 'Un sonido suave o campana para marcar el cambio'],
    },
    prompts: {
      en: [
        'Find a partner. Stand facing each other, an arm\'s length apart.',
        'One leads. Move so slowly your partner can follow without missing a thing.',
        'When the chime rings, switch. The leader becomes the mirror.',
      ],
      es: [
        'Encuentra una pareja. Mírense de frente, a un brazo de distancia.',
        'Uno guía. Muévete tan despacio que tu pareja pueda seguirte sin perder nada.',
        'Cuando suene la campana, cambien. El líder se vuelve el espejo.',
      ],
    },
    vocabulary: {
      en: ['mirror', 'follow', 'lead', 'slow', 'gentle', 'partner'],
      es: ['espejo', 'seguir', 'guiar', 'lento/a', 'suave', 'pareja'],
    },
  },

  // ── SOUND ──────────────────────────────────────────────────────────
  {
    id: 'sound-found-orchestra',
    category: 'sound',
    name: { en: 'Found-object orchestra', es: 'Orquesta de objetos encontrados' },
    duration: 10,
    description: {
      en: 'Each child finds one object that makes a sound — a stick on a chair, paper crumpled, a cup tapped. The "conductor" (one child at a time) raises a hand to bring sounds in, lowers it to fade them out. Teacher protects the slow rise + fall.',
      es: 'Cada niño/a busca un objeto que haga sonido — un palo en una silla, papel arrugado, una taza golpeada. El "director" (un niño/a por turno) levanta la mano para que entren los sonidos, la baja para apagarlos. La maestra/o protege la subida y bajada lenta.',
    },
    materials: {
      en: ['Whatever the room has — sticks, paper, cups, hands'],
      es: ['Lo que tenga el salón — palos, papel, tazas, manos'],
    },
    prompts: {
      en: [
        'Find your sound-maker. Try it once, quietly, alone.',
        'Sit in a circle. The conductor stands. Hand up = play. Hand down = silence.',
        'Take turns conducting. Each conductor gets ten slow seconds.',
      ],
      es: [
        'Encuentra tu objeto-sonido. Pruébalo una vez, en silencio, a solas.',
        'Siéntense en círculo. El director/a se pone de pie. Mano arriba = tocar. Mano abajo = silencio.',
        'Tomen turnos para dirigir. Cada director/a tiene diez segundos lentos.',
      ],
    },
    vocabulary: {
      en: ['sound', 'silence', 'loud', 'soft', 'rhythm', 'conductor', 'together'],
      es: ['sonido', 'silencio', 'fuerte', 'suave', 'ritmo', 'director/a', 'juntos'],
    },
  },
];

/** Localized human-readable category labels for filter chips. */
export const PLAY_CATEGORY_LABELS: Record<PlayCategory, { en: string; es: string }> = {
  nature: { en: 'Nature', es: 'Naturaleza' },
  pretend: { en: 'Pretend', es: 'Imaginar' },
  fort: { en: 'Forts', es: 'Fuertes' },
  drawing: { en: 'Drawing', es: 'Dibujo' },
  story: { en: 'Story', es: 'Cuento' },
  movement: { en: 'Movement', es: 'Movimiento' },
  sound: { en: 'Sound', es: 'Sonido' },
};
